import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { useMount, useUpdateEffect } from "react-use";
import { useRecoilValue, useSetRecoilState } from "recoil";
import moment from "moment";
import { getLecture, getUserLectureList, uploadSurvey } from "../../api";
import { Button, LinkButton } from "../../components/common/Forms";
import Icon from "../../components/common/Icon";
import {
  getItemButtons,
  getStatusBadge,
  useLocationQuery,
} from "../../util/common";
import SurveyViewModal from "../../components/survey/SurveyViewModal";
import {
  surveyFocusIndex,
  surveyInfoContents,
  surveyInfoTitle,
  surveyList,
} from "../../states/atoms/surveyState";
import SurveyAddModal from "../../components/survey/SurveyAddModal";
import { signItem } from "../../states/atoms/signState";

const SurveyListPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const query = useLocationQuery();
  const [{ sort, asc }, setSort] = useState({ sort: null, asc: true });
  const [lectureList, setLectures] = useState([]);
  const [selectedLecture, setSelected] = useState(undefined);
  const [contents, setQueryResult] = useState([]);
  const [viewModal, setViewModal] = useState({ open: false, data: null });
  const [addModal, setAddModal] = useState({ open: false, data: null });
  const [auth, setAuth] = useState(false);
  const setTitle = useSetRecoilState(surveyInfoTitle);
  const setContents = useSetRecoilState(surveyInfoContents);
  const setList = useSetRecoilState(surveyList);
  const setFocus = useSetRecoilState(surveyFocusIndex);
  const signInfo = useRecoilValue(signItem);

  const uploadMttn = useMutation({
    mutationFn: uploadSurvey,
    onSettled: async (data) => {
      setViewModal({ ...viewModal, error: false });
      if (data.result) {
        setViewModal({
          ...viewModal,
          loading: false,
          result: "설문 결과 업로드가 완료되었습니다.",
        });
      } else {
        setViewModal({
          ...viewModal,
          loading: false,
          result: "설문 결과 업로드 중 오류가 발생했습니다.",
          error: true,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["survey/upload"] });
    },
  });
  const handleAddClose = () => {
    setAddModal({ open: false, data: null });
  };

  const handleAdd = () => {
    const modalList = [...lectureList].filter(({ _id }) => _id !== "all");
    modalList.unshift({
      _id: null,
      title: "설문을 추가할 강의를 선택하세요.",
    });
    setAddModal({
      open: true,
      data: {
        lectureList: modalList,
        selectedLecture,
      },
      onAdd: ({ target, lecture }) => {
        navigate(`/survey/add/${lecture}?target=${target}`);
        handleAddClose();
      },
      onClose: handleAddClose,
    });
  };

  const handleViewUpload = ({ id, files }) => {
    uploadMttn.mutate({ id, files });
  };

  const handleViewClose = () => {
    setViewModal({ open: false, data: null });
    getListQuery(auth);
  };

  const handleViewUpdate = (id, target) => {
    navigate(`/survey/update/${id}?target=${target}`);
    handleViewClose();
  };

  const handleViewDuplicate = (data) => {
    const {
      data: { title, contents, list },
      target,
      lecture_id,
    } = data;
    setTitle(title);
    setContents(contents);
    setList(list);
    setFocus(0);
    navigate(`/survey/add/${lecture_id}?target=${target}`);
  };

  const handleSelect = (e) => {
    setSelected(e.target.value);
  };

  const handleSort = (key) => () => {
    if (sort === key) {
      setSort({ sort, asc: !asc });
    } else {
      setSort({ sort: key, asc: true });
    }
  };

  const getListQuery = async (role) => {
    const lecture_id = selectedLecture || query.lecture;
    if (selectedLecture == null && query.lecture) {
      setSelected(query.lecture);
    }
    const result = await queryClient.fetchQuery(["survey/list"], {
      queryFn: () => getLecture({ lecture_id, sort, asc }),
    });
    const list = await getLectureListQuery();
    if (result.result) {
      const data = result.data.map((listitem) => {
        const {
          _id: id,
          title,
          start_date: sd,
          end_date: ed,
          survey_parent,
          survey_student,
        } = listitem;
        const start_date = new moment(sd).format("YYYY-MM-DD HH:mm");
        const end_date = new moment(ed).format("YYYY-MM-DD HH:mm");
        const modalList = [...list].filter(({ _id }) => _id !== "all");
        modalList.unshift({
          _id: null,
          title: "설문을 추가할 강의를 선택하세요.",
        });
        return {
          key: `row_${id}`,
          item: [
            {
              key: `r${id}_cell_title`,
              value: id,
              width: "w-auto",
              label: (
                <LinkButton
                  onClick={() =>
                    setViewModal({
                      open: true,
                      auth: role,
                      data: {
                        id,
                        parent: survey_parent,
                        student: survey_student,
                        lectureList: modalList,
                      },
                      onClose: handleViewClose,
                      onDuplicate: handleViewDuplicate,
                      onUpdate: handleViewUpdate,
                      onUpload: handleViewUpload,
                    })
                  }
                >
                  {title}
                </LinkButton>
              ),
            },
            {
              ...getStatusBadge(start_date, end_date),
              key: `r${id}_cell_status`,
              width: "w-auto text-center",
            },
            {
              ...getItemButtons({
                item: "survey",
                data: survey_parent,
                id,
                target: "parent",
                auth: role,
              }),
              key: `r${id}_cell_parent_survey`,
              width: "w-auto text-center",
            },
            {
              ...getItemButtons({
                item: "survey",
                data: survey_student,
                id,
                target: "student",
                auth: role,
              }),
              key: `r${id}_cell_student_survey`,
              width: "w-auto text-center",
            },
          ],
        };
      });
      setQueryResult(data);
    }
    queryClient.invalidateQueries(["survey/list"]);
  };

  const getLectureListQuery = async () => {
    const result = await queryClient.fetchQuery(["survey/lecture/list"], {
      queryFn: getUserLectureList,
    });
    if (result.result) {
      const arr = [...result.data];
      arr.unshift({ _id: "all", title: "전체보기" });
      return arr;
    }
    return [];
  };

  useEffect(() => {
    if (signInfo && "info" in signInfo) {
      if (signInfo.info && "role" in signInfo.info) {
        const { role } = signInfo.info;
        return setAuth(role === "admin");
      }
    }
    setAuth(false);
  }, [signInfo]);

  useUpdateEffect(() => {
    getListQuery(auth);
  }, [selectedLecture, sort, asc, auth]);

  useMount(async () => {
    const list = await getLectureListQuery();
    setLectures(list);
  });

  return (
    <div className="w-full mx-auto md:w-11/12 md:px-2">
      <h1 className="flex items-center font-sans font-bold break-normal text-cyan-500 px-2 py-4 text-xl md:text-2xl">
        설문 관리
      </h1>
      <div className="p-4 md:p-8 lg:mt-0 rounded shadow bg-white">
        <div className="w-full flex gap-2 md:flex-row flex-col justify-between md:items-center mb-2 sticky top-0 left-0 right-0">
          <div className="inline-flex w-full md:w-1/2 items-center">
            <label
              htmlFor="classes"
              className="w-20 block text-sm font-medium text-gray-700"
            >
              강의 선택
            </label>
            <select
              id="classes"
              name="classes"
              autoComplete="classes-name"
              className="mt-1 block w-full rounded-md border border-gray-300 bg-white py-2 px-3 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-cyan-500 sm:text-sm"
              onChange={handleSelect}
              value={selectedLecture}
            >
              {lectureList.map(({ _id: id, title: v }) => (
                <option key={`option_${v}`} value={id}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          {auth && (
            <Button
              type="primary"
              size="sm"
              className="self-end w-1/2 md:w-auto"
              onClick={handleAdd}
            >
              <Icon name="plus" size="sm" />
              설문 추가
            </Button>
          )}
        </div>
        <div className="rounded overflow-scroll scrollbar-hide">
          <table className="stripe rounded hover min-w-full w-max py-4 text-sm table-auto border-collapse border border-zinc-400">
            <thead className="rounded-t">
              <tr className="cursor-default">
                <th
                  className="w-auto py-2 border border-zinc-200 bg-zinc-100"
                  onClick={handleSort("title")}
                >
                  <div className="inline-flex items-center">
                    강의 이름
                    {sort === "title" && (
                      <Icon
                        name={asc ? "bars-arrow-down" : "bars-arrow-up"}
                        size="sm"
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-auto py-2 border border-zinc-200 bg-zinc-100"
                  onClick={handleSort("status")}
                >
                  <div className="inline-flex items-center">
                    상태
                    {sort === "status" && (
                      <Icon
                        name={asc ? "bars-arrow-down" : "bars-arrow-up"}
                        size="sm"
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-auto py-2 border border-zinc-200 bg-zinc-100"
                  onClick={handleSort("survey_parent")}
                >
                  <div className="inline-flex items-center">
                    학부모
                    {sort === "survey_parent" && (
                      <Icon
                        name={asc ? "bars-arrow-down" : "bars-arrow-up"}
                        size="sm"
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
                <th
                  className="w-auto py-2 border border-zinc-200 bg-zinc-100"
                  onClick={handleSort("survey_student")}
                >
                  <div className="inline-flex items-center">
                    학생
                    {sort === "survey_student" && (
                      <Icon
                        name={asc ? "bars-arrow-down" : "bars-arrow-up"}
                        size="sm"
                        className="ml-1"
                      />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="rounded-b">
              {contents.length > 0 &&
                contents.map(({ key, item }) => (
                  <tr key={key}>
                    {item.map(({ key, width, label }) => (
                      <td
                        {...{
                          key,
                          className: `border border-zinc-300 p-1 ${width}`,
                        }}
                      >
                        {label}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <SurveyViewModal {...{ ...viewModal }} />
      <SurveyAddModal {...{ ...addModal }} />
    </div>
  );
};

export default SurveyListPage;
